
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';
import { useEffect } from 'react';

export default function PricingPage() {
  useEffect(() => {
    // Schema.org JSON-LD for WebPage and Service
    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "料金について - 訪問ステーションあすなろ",
      "description": "訪問看護サービスの料金体系を詳しくご説明。介護保険・医療保険それぞれの料金設定や自己負担割合について分かりやすく解説します。",
      "url": `${import.meta.env.VITE_SITE_URL}/pricing`,
      "mainEntity": {
        "@type": "Service",
        "name": "訪問看護サービス",
        "description": "介護保険・医療保険を利用した訪問看護サービス",
        "provider": {
          "@type": "Organization",
          "name": "株式会社あすなろ"
        },
        "offers": [
          {
            "@type": "Offer",
            "name": "看護師による訪問看護（20分未満）",
            "price": "336",
            "priceCurrency": "JPY",
            "description": "短時間の医療処置や健康チェック"
          },
          {
            "@type": "Offer",
            "name": "看護師による訪問看護（30分未満）",
            "price": "504", 
            "priceCurrency": "JPY",
            "description": "基本的な看護ケアと観察"
          },
          {
            "@type": "Offer",
            "name": "リハビリ職による訪問（20分）",
            "price": "315",
            "priceCurrency": "JPY",
            "description": "基本的なリハビリテーション"
          }
        ]
      }
    };

    const existingScript = document.getElementById('pricing-webpage-schema');
    if (existingScript) {
      existingScript.remove();
    }
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'pricing-webpage-schema';
    script.textContent = JSON.stringify(webPageSchema);
    document.head.appendChild(script);

    // Update page title and meta
    document.title = "料金について - 訪問ステーションあすなろ";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '訪問看護サービスの料金体系を詳しくご説明。介護保険・医療保険それぞれの料金設定や自己負担割合について分かりやすく解説します。');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', '料金,訪問看護,介護保険,医療保険,自己負担,費用');
    }

    return () => {
      document.getElementById('pricing-webpage-schema')?.remove();
    };
  }, []);

  const nursingPrices = [
    {
      duration: '20分未満',
      price: '336円',
      description: '短時間の医療処置や健康チェック'
    },
    {
      duration: '30分未満',
      price: '504円',
      description: '基本的な看護ケアと観察'
    },
    {
      duration: '30分〜60分未満',
      price: '881円',
      description: '総合的な看護ケアとリハビリ'
    },
    {
      duration: '60分〜90分未満',
      price: '1,207円',
      description: '包括的なケアと家族指導'
    }
  ];

  const rehabilitationPrices = [
    {
      duration: '20分',
      price: '315円',
      description: '基本的なリハビリテーション'
    },
    {
      duration: '40分',
      price: '630円',
      description: '標準的なリハビリテーション'
    },
    {
      duration: '60分',
      price: '852円',
      description: '包括的なリハビリテーション'
    }
  ];

  const selfPaymentRates = [
    { rate: '1割負担', description: '一般的な負担割合' },
    { rate: '2割負担', description: '一定以上の所得がある方' },
    { rate: '3割負担', description: '現役並み所得者' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* ヘッダー */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 cursor-pointer">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center">
                <i className="ri-heart-pulse-line text-2xl text-white"></i>
              </div>
              <span className="text-2xl font-bold text-gray-900">訪問ステーションあすなろ</span>
            </Link>
            <Link
              to="/#contact"
              className="px-8 py-3 bg-teal-600 text-white rounded-xl font-semibold hover:bg-teal-700 transition-colors shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              お問い合わせ
            </Link>
          </div>
        </div>
      </header>

      <main>
        {/* ヒーローセクション */}
        <section className="relative py-24 bg-gradient-to-br from-teal-50 via-blue-50 to-purple-50 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-72 h-72 bg-teal-400 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-400 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-7xl mx-auto px-6 relative">
            <div className="text-center">
              <span className="inline-block px-6 py-2 bg-white/80 backdrop-blur-sm text-teal-700 rounded-full text-lg font-semibold mb-6 shadow-lg">
                Pricing
              </span>
              <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-8">
                料金について
              </h1>
              <p className="text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                介護保険を利用した訪問看護サービスの料金体系を<br />
                わかりやすくご説明いたします。
              </p>
            </div>
          </div>
        </section>

        {/* 介護保険料金表 */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl text-2xl font-bold shadow-lg mb-8">
                <i className="ri-shield-check-line text-3xl"></i>
                介護保険でご利用の場合
              </div>
            </div>

            {/* 看護師による訪問看護 */}
            <div className="mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">看護師による訪問看護</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto text-center mb-12">
                1回のサービス時間に応じた料金設定となっております
              </p>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                {nursingPrices.map((item, index) => (
                  <div 
                    key={index}
                    className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 border-2 border-gray-100 hover:border-teal-200 hover:shadow-xl transition-all duration-300 text-center"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <i className="ri-time-line text-3xl text-white"></i>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {item.duration}
                    </h3>
                    
                    <div className="text-3xl font-bold text-teal-600 mb-4">
                      {item.price}
                    </div>
                    
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* リハビリ職による訪問 */}
            <div className="mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">リハビリ職による訪問</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto text-center mb-4">
                理学療法士・作業療法士・言語聴覚士による専門的なリハビリテーション
              </p>
              <p className="text-lg text-gray-500 text-center mb-12">
                ※1週あたり120分まで
              </p>

              <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
                {rehabilitationPrices.map((item, index) => (
                  <div 
                    key={index}
                    className="bg-gradient-to-br from-white to-blue-50 rounded-2xl p-8 border-2 border-blue-100 hover:border-blue-200 hover:shadow-xl transition-all duration-300 text-center"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <i className="ri-run-line text-3xl text-white"></i>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {item.duration}
                    </h3>
                    
                    <div className="text-3xl font-bold text-blue-600 mb-4">
                      {item.price}
                    </div>
                    
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* 自己負担割合説明 */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-3xl p-8 border border-blue-200">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">自己負担割合について</h3>
                <p className="text-lg text-gray-700">
                  上記料金は介護保険の基準額です。実際のお支払い額は、お客様の負担割合によって決まります。
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {selfPaymentRates.map((rate, index) => (
                  <div key={index} className="bg-white rounded-2xl p-6 text-center shadow-lg">
                    <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <i className="ri-percent-line text-2xl text-white"></i>
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">{rate.rate}</h4>
                    <p className="text-gray-600 text-sm">{rate.description}</p>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </section>

        {/* 医療保険について */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl text-2xl font-bold shadow-lg mb-8">
                <i className="ri-hospital-line text-3xl"></i>
                医療保険でご利用の場合
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">医療保険による訪問看護</h2>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 mb-8">
                <div className="text-center mb-8">
                  <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <i className="ri-stethoscope-line text-4xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">サービス時間と料金</h3>
                  <div className="bg-red-50 rounded-2xl p-6 border border-red-200 mb-6">
                    <div className="text-center">
                      <h4 className="text-xl font-bold text-gray-900 mb-3">1回のサービス時間</h4>
                      <div className="text-3xl font-bold text-red-600 mb-4">30分〜90分</div>
                      <p className="text-gray-600">疾患や治療内容に応じて調整いたします</p>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-200">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-information-line text-2xl text-white"></i>
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-gray-900 mb-3">医療保険の料金について</h4>
                        <p className="text-lg text-amber-700 font-semibold">
                          医療保険の料金に関してはパターンが多岐に渡り複雑なため、省略させていただきます。詳しくはお問い合わせくださいませ。
                        </p>
                      </div>
                    </div>
                  </div>

                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-red-50 rounded-2xl p-6 border border-red-200">
                    <h4 className="text-lg font-bold text-gray-900 mb-3">対象となる方</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-red-500 text-lg mt-0.5"></i>
                        <span>40歳未満の方</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-red-500 text-lg mt-0.5"></i>
                        <span>特定疾患の方</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-red-500 text-lg mt-0.5"></i>
                        <span>急性期・回復期の方</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-red-50 rounded-2xl p-6 border border-red-200">
                    <h4 className="text-lg font-bold text-gray-900 mb-3">自己負担割合</h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-red-500 text-lg mt-0.5"></i>
                        <span>一般的に1〜3割負担</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-red-500 text-lg mt-0.5"></i>
                        <span>年齢や所得により決定</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-red-500 text-lg mt-0.5"></i>
                        <span>高額療養費制度適用</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 追加料金・オプション */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16">
              <span className="inline-block px-5 py-2 bg-teal-50 text-teal-700 rounded-full text-lg font-semibold mb-6">
                Additional Services
              </span>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">その他のサービス</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                基本サービスに加えて、以下のサービスもご利用いただけます
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-car-line',
                  title: '緊急時対応',
                  description: '24時間365日の緊急時対応体制',
                  note: '基本料金に含まれます'
                },
                {
                  icon: 'ri-phone-line',
                  title: '電話相談',
                  description: '看護師による電話での健康相談',
                  note: '基本料金に含まれます'
                },
                {
                  icon: 'ri-team-line',
                  title: '多職種連携',
                  description: '医師・ケアマネージャーとの連携',
                  note: '基本料金に含まれます'
                },
                {
                  icon: 'ri-hospital-line',
                  title: '病院同行サポート',
                  description: '精神疾患の方への病院同行',
                  note: '月1回まで無料'
                },
                {
                  icon: 'ri-heart-line',
                  title: '精神的サポート',
                  description: '心のケアと精神的支援',
                  note: '基本料金に含まれます'
                },
                {
                  icon: 'ri-user-heart-line',
                  title: '家族支援',
                  description: 'ご家族への介護指導・相談',
                  note: '基本料金に含まれます'
                }
              ].map((service, index) => (
                <div 
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-100 hover:border-teal-200 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center mb-4">
                    <i className={`${service.icon} text-2xl text-white`}></i>
                  </div>
                  
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-3 text-sm leading-relaxed">
                    {service.description}
                  </p>
                  
                  <div className="inline-block px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-semibold">
                    {service.note}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 注意事項 */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-4xl mx-auto px-6">
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-8 border border-amber-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">料金に関するご注意</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-amber-600 text-lg mt-0.5"></i>
                      <span>料金は介護保険・医療保険の改定により変更される場合があります</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-amber-600 text-lg mt-0.5"></i>
                      <span>お客様の負担割合は、介護保険証・医療保険証でご確認ください</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-amber-600 text-lg mt-0.5"></i>
                      <span>詳細な料金については、ご契約前に必ずご説明いたします</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-arrow-right-s-line text-amber-600 text-lg mt-0.5"></i>
                      <span>ご不明な点がございましたら、お気軽にお問い合わせください</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTAセクション */}
        <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-5xl font-bold text-white mb-6">
              料金についてのご相談
            </h2>
            <p className="text-2xl text-white/90 mb-10 leading-relaxed">
              お客様の状況に応じた詳細な料金をご案内いたします。<br />
              まずはお気軽にお問い合わせください。
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <a
                href="tel:0463-74-4834"
                className="px-10 py-5 bg-white text-teal-700 rounded-xl font-bold text-xl hover:bg-gray-50 transition-colors shadow-2xl hover:shadow-3xl whitespace-nowrap cursor-pointer inline-flex items-center gap-3"
              >
                <i className="ri-phone-line text-2xl"></i>
                0463-74-4834
              </a>
              <Link
                to="/#contact"
                className="px-10 py-5 bg-teal-800 text-white rounded-xl font-bold text-xl hover:bg-teal-900 transition-colors shadow-2xl hover:shadow-3xl whitespace-nowrap cursor-pointer inline-flex items-center gap-3"
              >
                <i className="ri-mail-line text-2xl"></i>
                メールで問い合わせ
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
